import { useState } from "react";
import { Link } from "react-router-dom";
import { FileText } from "lucide-react";
import { toast } from "sonner";
import { Reveal, ImageReveal } from "../shared/Reveal";
import { Field, PrivacyCheck, SuccessBox } from "../shared/Form";
import { api, imgUrl, isPdf, formatApiError } from "../../lib/api";
import { Placeholder } from "./OperationCard";
import { MEDIA_TABS } from "./operationsData";

const itemsFor = (o, key) => (key === "project" ? [...(o.renders || []), ...(o.floor_plans || [])] : o[key] || []);

export const OperationMedia = ({ o }) => {
  const firstFilled = MEDIA_TABS.find((t) => itemsFor(o, t.key).length)?.key || "images_before";
  const [tab, setTab] = useState(firstFilled);
  const items = itemsFor(o, tab);
  const t = MEDIA_TABS.find((x) => x.key === tab);
  return (
    <section className="section bg-cream" id="galleria">
      <div className="container-hh">
        <Reveal className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12 md:mb-16">
          <div><div className="eyebrow mb-5">Prima / Progetto / Dopo</div><h2 className="h-section">L'immobile, passo dopo passo.</h2></div>
          <div className="flex flex-wrap gap-2" data-testid="op-media-tabs">
            {MEDIA_TABS.map((x) => <button key={x.key} type="button" onClick={() => setTab(x.key)} data-testid={`op-media-tab-${x.key}`} className={`px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] border transition-colors duration-300 ${tab === x.key ? "bg-ink text-cream border-ink" : "border-ink/20 text-ink hover:border-gold"}`}>{x.label}<span className="ml-2 opacity-50">{itemsFor(o, x.key).length || ""}</span></button>)}
          </div>
        </Reveal>
        {items.length === 0 ? (
          <Reveal><Placeholder text={t.empty} className="w-full aspect-[4/3] md:aspect-[16/7]" /></Reveal>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8" data-testid="op-media-grid">
            {items.map((src, i) => isPdf(src) ? (
              <a key={src} href={imgUrl(src)} target="_blank" rel="noreferrer" data-testid={`op-media-pdf-${i}`} className="flex items-center gap-4 border border-ink/10 bg-white p-8 hover:border-gold transition-colors"><FileText strokeWidth={1} size={32} /><span className="text-sm uppercase tracking-[0.18em]">Planimetria {i + 1} (PDF)</span></a>
            ) : (
              <ImageReveal key={src} src={imgUrl(src)} alt={`${o.title} — ${t.label} ${i + 1}`} className={`${i % 3 === 0 ? "md:col-span-2 aspect-[16/9]" : "aspect-[4/3]"} bg-beige`} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export const InvestorForm = ({ o }) => {
  const [f, setF] = useState({ name: "", surname: "", email: "", phone: "", amount: "", message: "", privacy: false, website: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.privacy) return toast.error("È necessario accettare l'informativa privacy");
    setBusy(true);
    try {
      await api.post("/leads", { type: "operation", name: `${f.name} ${f.surname}`.trim(), email: f.email, phone: f.phone, city: o.city, message: f.message, details: { operazione: o.title, slug: o.slug, cognome: f.surname, importo_indicativo: f.amount, richiesta: "Dossier investitore" }, privacy_accepted: f.privacy, website: f.website });
      setSent(true);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <section id="dossier" className="section bg-white">
      <div className="container-hh grid grid-cols-1 lg:grid-cols-12 gap-14">
        <Reveal className="lg:col-span-5">
          <div className="eyebrow mb-5">Sezione investitore</div>
          <h2 className="h-section">Partecipa all'operazione.</h2>
          <p className="lead mt-8">{o.title} è un'operazione {o.status === "aperta" ? "aperta" : o.status === "in_corso" ? "in corso" : "conclusa"} Home Haven. Gli investitori interessati possono richiedere maggiori informazioni sul progetto, sul quadro economico, sulle modalità di partecipazione e sulle tempistiche previste.</p>
          <p className="text-sm font-light text-ink/60 mt-8">Condizioni economiche, quadro finanziario e modalità di partecipazione vengono condivisi esclusivamente su richiesta, in forma riservata. Nessun rendimento è garantito.</p>
          <div className="flex flex-wrap gap-x-8 gap-y-3 mt-8"><Link to="/investitori" data-testid="op-link-investitori" className="btn-link">Come investire con Home Haven</Link><Link to="/contatti" data-testid="op-link-contatti" className="btn-link">Contatti</Link></div>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-7">
          {sent ? <SuccessBox testId="op-form-success" title="Richiesta ricevuta." text={`Il team Home Haven ti ricontatterà per condividere il dossier investitore dell'operazione ${o.title}.`} /> : (
            <form onSubmit={submit} data-testid="op-investor-form" className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Field label="Nome" name="name" value={f.name} onChange={set} required testId="op-name" />
              <Field label="Cognome" name="surname" value={f.surname} onChange={set} required testId="op-surname" />
              <Field label="Email" name="email" type="email" value={f.email} onChange={set} required testId="op-email" />
              <Field label="Telefono" name="phone" type="tel" value={f.phone} onChange={set} testId="op-phone" />
              <div className="md:col-span-2"><Field label="Importo indicativo disponibile" name="amount" value={f.amount} onChange={set} placeholder="Es. € 10.000" testId="op-amount" /></div>
              <div className="md:col-span-2"><Field label="Messaggio" name="message" as="textarea" value={f.message} onChange={set} placeholder="Raccontaci il tuo interesse per l'operazione…" testId="op-message" /></div>
              <input type="text" name="website" value={f.website} onChange={(e) => set("website", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" />
              <div className="md:col-span-2"><PrivacyCheck checked={f.privacy} onChange={(v) => set("privacy", v)} testId="op-privacy" /></div>
              <div className="md:col-span-2"><button type="submit" disabled={busy} data-testid="op-submit" className="btn-primary disabled:opacity-50">{busy ? "Invio…" : "Richiedi informazioni"}</button></div>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
